import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../../../../styles/panel/user/manageShop/EditShopInfo.css";
import {
  fetchStoreBasicInfo,
  updateStoreBasicInfo,
  checkSlug,
} from "../../../../api/storeManagement/storeManagement";
import {
  fetchProvinces,
  fetchCities,
  fetchStoreTypes,
} from "../../../../api/storeRequest";
import { API_BASE_URL } from "../../../../api/http";
import { swalError, swalSuccess } from "../../../../utils/swalAlert";

// حداکثر حجم مجاز برای تصاویر (۳ مگابایت)
const MAX_IMAGE_SIZE = 2 * 1024 * 1024;
import {
  BACK_ICON,
  CHECK_ICON,
  SPINNER_ICON,
  STORE_ICON,
  UPLOAD_ICON,
  X_ICON,
  Icon,
} from "./shared/EditShopShared";

function slugify(value) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function FilePicker({ label, hint, value, onChange, shape = "square" }) {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  // value می‌تونه یا یک File تازه‌انتخاب‌شده باشه، یا یک رشته URL که از سرور اومده
  const isFile = value instanceof File || value instanceof Blob;
  const previewUrl = isFile
    ? URL.createObjectURL(value)
    : typeof value === "string" && value
      ? value.startsWith("http")
        ? value
        : `${API_BASE_URL}${value}`
      : null;

  // آبجکت URL موقتی رو بعد از آنماونت/تغییر پاک می‌کنیم که حافظه لیک نشه
  useEffect(() => {
    return () => {
      if (isFile && previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl, isFile]);

  // بررسی حجم فایل قبل از ست کردن؛ اگه بیشتر از حد مجاز بود ارور نشون بده
  const handleFile = (file) => {
    if (!file) {
      onChange(null);
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      swalError({
        text: "حجم عکس زیاده! حداکثر حجم مجاز 2 مگابایت هست.",
      });
      return;
    }
    onChange(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div className="esi-field">
      <label className="esi-label">{label}</label>
      <div
        className={`esi-dropzone esi-dropzone--${shape}${
          isDragging ? " is-dragging" : ""
        }`}
        onClick={() => inputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {previewUrl ? (
          <div className="esi-selected-file">
            <img
              src={previewUrl}
              alt={label}
              className="esi-file-preview-img"
            />
            <span>{isFile ? value.name : "عکس فعلی"}</span>
          </div>
        ) : (
          <>
            <div className="esi-dropzone-icon">
              <Icon className="esi-file-ic">{UPLOAD_ICON}</Icon>
            </div>
            <p className="esi-dropzone-title">فایل را اینجا بکشید و رها کنید</p>
            {/* <span className="esi-dropzone-sub">{hint}</span> */}
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => handleFile(e.target.files?.[0] || null)}
        />
      </div>
    </div>
  );
}

export default function EditShopInfo() {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const onBack = () => navigate(-1);

  const [form, setForm] = useState({});
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [storeTypes, setStoreTypes] = useState([]);
  const [addressStatus, setAddressStatus] = useState("idle");
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);

  // فیلدهای اجباری فرم
  const requiredFields = [
    "store_name",
    "store_slug",
    "store_type",
    "province",
    "city",
    "mobile",
    "address",
  ];

  const isEmpty = (val) =>
    val === undefined || val === null || String(val).trim() === "";

  const errors = requiredFields.reduce((acc, key) => {
    if (isEmpty(form[key])) acc[key] = "این فیلد الزامی است";
    return acc;
  }, {});

  const markTouched = (field) => setTouched((t) => ({ ...t, [field]: true }));

  const showError = (field) => (touched[field] || submitted) && errors[field];
  /*
   * استخراج پیام خطا از پاسخ سرور
   */
  const getErrorMessage = (err, fallback) => {
    return (
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.response?.data?.detail ||
      (typeof err?.response?.data === "string" ? err.response.data : null) ||
      err?.message ||
      fallback
    );
  };

  useEffect(() => {
    const load = async () => {
      const store = await fetchStoreBasicInfo(shopId);
      console.log(store);

      setForm(store.data || {});

      // به محض ورود به صفحه، آدرس فعلی فروشگاه رو با سرور چک می‌کنیم
      if (store.data?.store_slug) {
        checkingAddress(store.data.store_slug);
      }

      const p = await fetchProvinces();
      setProvinces(p?.data || []);

      const st = await fetchStoreTypes();
      setStoreTypes(st?.data || []);

      if (store.data?.province) {
        const c = await fetchCities(store.data.province);
        setCities(c?.data || []);
      }
    };
    load();
  }, [shopId]);

  useEffect(() => {
    if (!form.province) return setCities([]);
    const loadCities = async () => {
      const res = await fetchCities(form.province);
      setCities(res?.data || []);
    };
    loadCities();
  }, [form.province]);

  const onChangeProvince = (e) => {
    const provinceId = e.target.value;
    const selected = provinces.find(
      (p) => String(p.province_id) === String(provinceId),
    );
    console.log("selected ===>", selected);
    setForm({
      ...form,
      province: provinceId,
      province_name: selected?.name || "",
      city: "",
      city_title: "",
    });
    setCities([]);
  };

  const onChangeCity = (e) => {
    const cityId = e.target.value;
    const selected = cities.find((c) => String(c.city_id) === String(cityId));
    setForm({
      ...form,
      city: cityId,
      city_title: selected?.name || "",
    });
  };

  const onChangeStoreType = (e) => {
    const typeId = e.target.value;
    const selected = storeTypes.find(
      (t) => String(t.type_id) === String(typeId),
    );
    setForm({
      ...form,
      store_type: typeId,
      store_type_title: selected?.title || "",
    });
  };

  const update = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  // چک اینکه آدرس فروشگاه (اسلاگ) از سمت بکند آزاده یا قبلاً گرفته شده
  // دقیقاً مثل الگوی StoreStep.jsx: روی onBlur اینپوت فراخوانی می‌شود
  const checkingAddress = async (slug) => {
    if (!slug || !slug.trim()) {
      setAddressStatus("idle");
      return;
    }

    setAddressStatus("checking");

    try {
      const res = await checkSlug(slug.trim(), shopId);
      setAddressStatus(res.data.exists ? "taken" : "available");
    } catch {
      setAddressStatus("idle");
    }
  };

  const handleAddressChange = (raw) => {
    const slug = slugify(raw);
    update("store_slug", slug);
    setAddressStatus("idle");
  };

  const handleProvinceChange = (province) => {
    setForm((f) => ({ ...f, province, city: "" }));
  };

  const allFilled = Object.keys(errors).length === 0;

  const canSubmit =
    allFilled && addressStatus !== "taken" && addressStatus !== "checking";
  const submit = async () => {
    if (!allFilled) return;
    const data = new FormData();
    const fields = [
      "store_name",
      "store_slug",
      "store_type",
      "province",
      "city",
      "phone",
      "mobile",
      "address",
    ];
    fields.forEach((key) => {
      if (form[key] !== undefined && form[key] !== null)
        data.append(key, form[key]);
    });
    if (form.logo instanceof File) {
      data.append("logo", form.logo);
    }
    if (form.store_image instanceof File) {
      data.append("store_image", form.store_image);
    }
    try {
      await updateStoreBasicInfo(shopId, data);
      await swalSuccess({
        title: "ویرایش شد",
        text: "ویرایش با موفقیت انجام شد",
      });
      // navigate("/app/store-management");
    } catch (err) {
      swalError({
        text: getErrorMessage(err) || "خطایی در انجام عملیات رخ داد.",
      });
    }
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!canSubmit) return;
    submit();
  };

  return (
    <div className="shop-edit-info">
      <div className="esi-head">
        <button type="button" className="esp-back-btn" onClick={onBack}>
          <Icon>{BACK_ICON}</Icon>
        </button>
        <div>
          <h1>ویرایش اطلاعات پایه فروشگاه</h1>
          <p>اطلاعات کلی، تصاویر و آدرس تماس فروشگاهت رو تنظیم کن.</p>
        </div>
      </div>

      <form className="esi-card" onSubmit={handleSubmit}>
        <div className="esi-section">
          <h3 className="esi-section-title">
            <Icon>{STORE_ICON}</Icon>
            هویت فروشگاه
          </h3>

          <div className="esi-grid">
            <div className="esi-field">
              <label className="esi-label" htmlFor="esi-name">
                نام فروشگاه
              </label>
              <input
                id="esi-name"
                type="text"
                className={`esi-input${showError("store_name") ? " esi-input--error" : ""}`}
                placeholder="مثلاً فروشگاه رنگین‌کمان"
                value={form.store_name || ""}
                onChange={(e) => update("store_name", e.target.value)}
                onBlur={() => markTouched("store_name")}
              />
              {showError("store_name") && (
                <span className="esi-hint esi-hint--taken">
                  {errors.store_name}
                </span>
              )}
            </div>

            <div className="esi-field">
              <label className="esi-label" htmlFor="esi-slug">
                آدرس فروشگاه
              </label>
              <div
                className={`esi-input-wrap esi-input-wrap--${addressStatus}`}
              >
                {/* <span className="esi-domain-suffix">.example.com</span> */}
                <input
                  id="esi-slug"
                  type="text"
                  className="esi-input esi-input--ltr"
                  placeholder="shop-name"
                  value={form.store_slug || ""}
                  onChange={(e) => handleAddressChange(e.target.value)}
                  onBlur={() => {
                    markTouched("store_slug");
                    checkingAddress(form.store_slug);
                  }}
                  dir="ltr"
                />
                <span className="esi-status-ic">
                  {addressStatus === "checking" && (
                    <Icon className="esi-spin">{SPINNER_ICON}</Icon>
                  )}
                  {addressStatus === "available" && <Icon>{CHECK_ICON}</Icon>}
                  {addressStatus === "taken" && <Icon>{X_ICON}</Icon>}
                </span>
              </div>
              <span className={`esi-hint esi-hint--${addressStatus}`}>
                {addressStatus === "checking" && "در حال بررسی..."}
                {addressStatus === "available" && "این آدرس در دسترسه ✓"}
                {addressStatus === "taken" &&
                  "این آدرس قبلاً استفاده شده، یه مورد دیگه امتحان کن"}
                {addressStatus === "idle" &&
                  (showError("store_slug")
                    ? errors.store_slug
                    : "فقط حروف انگلیسی، عدد، خط تیره و زیرخط")}
              </span>
            </div>
          </div>

          <div className="esi-grid">
            <FilePicker
              label="تصویر فروشگاه"
              hint="نمایش داده‌شده در صفحه اصلی فروشگاه"
              value={form.store_image}
              onChange={(f) => update("store_image", f)}
              shape="wide"
            />
            <FilePicker
              label="لوگو فروشگاه"
              hint="ترجیحاً تصویر مربعی، حداکثر ۲ مگابایت"
              value={form.logo}
              onChange={(f) => update("logo", f)}
              shape="square"
            />
          </div>
        </div>

        <div className="esi-section">
          <h3 className="esi-section-title">دسته‌بندی و موقعیت</h3>

          <div className="esi-grid esi-grid--2">
            <div className="esi-field">
              <label className="esi-label" htmlFor="esi-province">
                استان
              </label>
              <select
                id="esi-province"
                className={`esi-input esi-select${showError("province") ? " esi-input--error" : ""}`}
                value={form.province || ""}
                onChange={onChangeProvince}
                onBlur={() => markTouched("province")}
              >
                <option value="">انتخاب استان</option>
                {provinces.map((p) => (
                  <option key={p.province_id} value={p.province_id}>
                    {p.name}
                  </option>
                ))}
              </select>
              {showError("province") && (
                <span className="esi-hint esi-hint--taken">
                  {errors.province}
                </span>
              )}
            </div>

            <div className="esi-field">
              <label className="esi-label" htmlFor="esi-city">
                شهر
              </label>
              <select
                id="esi-city"
                className={`esi-input esi-select${showError("city") ? " esi-input--error" : ""}`}
                value={form.city || ""}
                onChange={onChangeCity}
                onBlur={() => markTouched("city")}
                disabled={!form.province}
              >
                <option value="">
                  {form.province ? "انتخاب کنید" : "ابتدا استان را انتخاب کنید"}
                </option>
                {cities.map((c) => (
                  <option key={c.city_id} value={c.city_id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {showError("city") && (
                <span className="esi-hint esi-hint--taken">{errors.city}</span>
              )}
            </div>
          </div>

          <div className="esi-grid">
            <div className="esi-field">
              <label className="esi-label" htmlFor="esi-category">
                دسته فروشگاه
              </label>
              <select
                id="esi-category"
                className={`esi-input esi-select${showError("store_type") ? " esi-input--error" : ""}`}
                value={form.store_type || ""}
                onChange={onChangeStoreType}
                onBlur={() => markTouched("store_type")}
              >
                <option value="">انتخاب کنید</option>
                {storeTypes.map((t) => (
                  <option key={t.type_id} value={t.type_id}>
                    {t.title}
                  </option>
                ))}
              </select>
              {showError("store_type") && (
                <span className="esi-hint esi-hint--taken">
                  {errors.store_type}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="esi-section">
          <h3 className="esi-section-title">اطلاعات تماس</h3>

          <div className="esi-grid esi-grid--2">
            <div className="esi-field">
              <label className="esi-label" htmlFor="esi-landline">
                شماره ثابت (اختیاری)
              </label>
              <input
                id="esi-landline"
                type="tel"
                className="esi-input esi-input--ltr"
                placeholder="021xxxxxxxx"
                dir="ltr"
                value={form.phone || ""}
                onChange={(e) => update("phone", e.target.value)}
              />
            </div>

            <div className="esi-field">
              <label className="esi-label" htmlFor="esi-mobile">
                شماره موبایل
              </label>
              <input
                id="esi-mobile"
                type="tel"
                className={`esi-input esi-input--ltr${showError("mobile") ? " esi-input--error" : ""}`}
                placeholder="09xxxxxxxxx"
                dir="ltr"
                value={form.mobile || ""}
                onChange={(e) => update("mobile", e.target.value)}
                onBlur={() => markTouched("mobile")}
              />
              {showError("mobile") && (
                <span className="esi-hint esi-hint--taken">
                  {errors.mobile}
                </span>
              )}
            </div>
          </div>

          <div className="esi-field">
            <label className="esi-label" htmlFor="esi-full-address">
              آدرس کامل
            </label>
            <textarea
              id="esi-full-address"
              className={`esi-input esi-textarea${showError("address") ? " esi-input--error" : ""}`}
              rows={3}
              placeholder="استان، شهر، خیابان، پلاک..."
              value={form.address || ""}
              onChange={(e) => update("address", e.target.value)}
              onBlur={() => markTouched("address")}
            />
            {showError("address") && (
              <span className="esi-hint esi-hint--taken">{errors.address}</span>
            )}
          </div>
        </div>

        <div className="esi-foot">
          <button type="button" className="esi-cancel-btn" onClick={onBack}>
            انصراف
          </button>
          <button type="submit" className="esi-save-btn" disabled={!canSubmit}>
            ذخیره تغییرات
          </button>
        </div>
      </form>
    </div>
  );
}
